import { useCallback, useEffect, useState } from 'react';
import {
  api,
  type ListResult,
  type FilterCondition,
  type SavedView,
  type CustomFieldDef,
} from '../api';
import { OPERATORS, type ObjectDef } from '../config';
import { RecordForm } from './RecordForm';
import { TimelineDrawer } from './TimelineDrawer';

const TIMELINE_OBJECTS = ['account', 'contact', 'deal'];

type Row = Record<string, unknown>;

function cell(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'object') return '';
  return String(value);
}

export function ResourceList({ object }: { object: ObjectDef }) {
  const [items, setItems] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [conditions, setConditions] = useState<FilterCondition[]>([]);
  const [views, setViews] = useState<SavedView[]>([]);
  const [customDefs, setCustomDefs] = useState<CustomFieldDef[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<string | undefined>(undefined);
  const [timelineId, setTimelineId] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  const pageSize = 25;

  const load = useCallback(
    async (toPage = page) => {
      setError(null);
      try {
        const res = await api.post<ListResult<Row>>(`/api/${object.path}/search`, {
          filter: { logic: 'and', conditions: conditions.filter((c) => c.field) },
          page: toPage,
          pageSize,
        });
        setItems(res.items);
        setTotal(res.total);
        setPage(res.page);
      } catch {
        setError('Failed to load records.');
      }
    },
    [object.path, conditions, page],
  );

  useEffect(() => {
    setConditions([]);
    setPage(1);
    void api.get<SavedView[]>(`/api/saved-views?objectType=${object.key}`).then(setViews).catch(() => undefined);
    void api.get<CustomFieldDef[]>(`/api/custom-fields/${object.key}`).then(setCustomDefs).catch(() => undefined);
  }, [object.key]);

  useEffect(() => {
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [object.path]);

  const filterFields = [
    ...object.filterFields,
    ...customDefs.map((d) => ({ key: `cf:${d.fieldKey}`, label: `${d.label} (custom)` })),
  ];

  function addCondition() {
    setConditions((p) => [...p, { field: filterFields[0]?.key ?? '', operator: 'contains', value: '' }]);
  }
  function updateCondition(i: number, patch: Partial<FilterCondition>) {
    setConditions((p) => p.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  }
  function removeCondition(i: number) {
    setConditions((p) => p.filter((_, idx) => idx !== i));
  }

  function applyView(id: string) {
    const v = views.find((x) => x.id === id);
    if (v) {
      setConditions(v.filterDef.conditions ?? []);
      setTimeout(() => void load(1), 0);
    }
  }

  async function saveView() {
    const name = window.prompt('Save current filter as view named:');
    if (!name) return;
    await api.post('/api/saved-views', {
      objectType: object.key,
      name,
      filterDef: { logic: 'and', conditions: conditions.filter((c) => c.field) },
    });
    setViews(await api.get<SavedView[]>(`/api/saved-views?objectType=${object.key}`));
  }

  async function remove(id: string) {
    if (!window.confirm('Delete this record?')) return;
    await api.del(`/api/${object.path}/${id}`);
    void load();
  }

  async function convertLead(id: string) {
    if (!window.confirm('Convert this lead into a contact? An account is created from the company if present.')) return;
    const createDeal = window.confirm('Also open a deal for the new contact?');
    try {
      await api.post(`/api/leads/${id}/convert`, { createDeal });
      setError(null);
      void load();
    } catch {
      setError('Convert failed.');
    }
  }

  async function exportList(format: 'csv' | 'xlsx' | 'pdf') {
    const res = await fetch('/api/export', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ object: object.key, format, filter: { logic: 'and', conditions: conditions.filter((c) => c.field) } }),
    });
    if (!res.ok) {
      setError('Export failed.');
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${object.path}.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="card">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1>{object.label}</h1>
        <button className="btn btn-primary" onClick={() => { setEditId(undefined); setFormOpen(true); }}>
          + New
        </button>
      </div>

      <div className="filters stack">
        {conditions.map((c, i) => (
          <div className="row" key={i}>
            <select value={c.field} onChange={(e) => updateCondition(i, { field: e.target.value })}>
              {filterFields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
            <select value={c.operator} onChange={(e) => updateCondition(i, { operator: e.target.value })}>
              {OPERATORS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {c.operator !== 'isEmpty' && c.operator !== 'isNotEmpty' && (
              <input
                value={(c.value as string) ?? ''}
                placeholder="value"
                onChange={(e) => updateCondition(i, { value: e.target.value })}
              />
            )}
            <button className="btn" onClick={() => removeCondition(i)}>✕</button>
          </div>
        ))}
        <div className="row">
          <button className="btn" onClick={addCondition}>+ Condition</button>
          <button className="btn btn-primary" onClick={() => void load(1)}>Apply</button>
          <button className="btn" onClick={() => { setConditions([]); setTimeout(() => void load(1), 0); }}>Clear</button>
          <button className="btn" onClick={() => void saveView()} disabled={!conditions.length}>Save view</button>
          <button className="btn" onClick={() => void exportList('csv')}>CSV</button>
          <button className="btn" onClick={() => void exportList('xlsx')}>Excel</button>
          <button className="btn" onClick={() => void exportList('pdf')}>PDF</button>
          {views.length > 0 && (
            <select defaultValue="" onChange={(e) => e.target.value && applyView(e.target.value)}>
              <option value="">Saved views…</option>
              {views.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          )}
        </div>
      </div>

      {error && <p style={{ color: '#b91c1c' }}>{error}</p>}

      <table className="grid">
        <thead>
          <tr>
            {object.columns.map((col) => <th key={col.key}>{col.label}</th>)}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr key={String(row.id)}>
              {object.columns.map((col) => <td key={col.key}>{cell(row[col.key])}</td>)}
              <td className="row">
                <button className="btn" onClick={() => { setEditId(String(row.id)); setFormOpen(true); }}>Edit</button>
                {object.key === 'lead' && String(row.status) !== 'converted' && (
                  <button className="btn" onClick={() => void convertLead(String(row.id))}>Convert</button>
                )}
                {TIMELINE_OBJECTS.includes(object.key) && (
                  <button className="btn" onClick={() => setTimelineId(String(row.id))}>Timeline</button>
                )}
                {object.key === 'meeting' && (
                  <button
                    className="btn"
                    onClick={() =>
                      api
                        .post(`/api/calendar/push/${String(row.id)}`)
                        .then(() => setError(null))
                        .catch(() => setError('Calendar sync failed — connect a mailbox/calendar first.'))
                    }
                  >
                    → Calendar
                  </button>
                )}
                <button className="btn" onClick={() => void remove(String(row.id))}>Delete</button>
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr><td colSpan={object.columns.length + 1} className="empty">No records.</td></tr>
          )}
        </tbody>
      </table>

      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span className="muted">{total} record(s)</span>
        <span className="row">
          <button className="btn" disabled={page <= 1} onClick={() => void load(page - 1)}>Prev</button>
          <span className="muted">Page {page} / {totalPages}</span>
          <button className="btn" disabled={page >= totalPages} onClick={() => void load(page + 1)}>Next</button>
        </span>
      </div>

      {formOpen && (
        <RecordForm
          object={object}
          recordId={editId}
          onClose={() => setFormOpen(false)}
          onSaved={() => { setFormOpen(false); void load(); }}
        />
      )}
      {timelineId && (
        <TimelineDrawer objectType={object.key} objectId={timelineId} onClose={() => setTimelineId(undefined)} />
      )}
    </div>
  );
}
